/**
 * LoanDrawer — right-side slide-out for viewing/editing a settled loan.
 * Tabs: Overview · Activity · Edit. Activity feed is append-only (no
 * delete/edit on past notes — that's the immutable log from Phase 2).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Loader2, Send, Phone, Mail, MessageSquare, FileText, Calendar, User as UserIcon, ShieldAlert, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
};

const TAB_LIST = [
  { k: 'overview', label: 'Overview' },
  { k: 'activity', label: 'Activity' },
  { k: 'nps',      label: 'NPS' },
  { k: 'messages', label: 'Messages' },
  { k: 'edit',     label: 'Edit' },
];

const LoanDrawer = ({ loanId, onClose, onUpdated }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('overview');
  const [loan, setLoan] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, ar] = await Promise.all([
        fetch(`${backendUrl}/api/settled-loans/${loanId}`, { headers: authHeaders }),
        fetch(`${backendUrl}/api/settled-loans/${loanId}/activities`, { headers: authHeaders }),
      ]);
      if (lr.ok) setLoan(await lr.json());
      if (ar.ok) setActivities(await ar.json());
    } finally { setLoading(false); }
  }, [backendUrl, authHeaders, loanId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-slate-950/40 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="loan-drawer">
      <aside className="w-full sm:w-[560px] max-w-full bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{loan?.loan_number || '…'}</div>
            <h2 className="text-lg font-bold truncate text-slate-900 dark:text-white">{loan?.client_name || (loading ? 'Loading…' : '')}</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {loan ? `${loan.lender} · ${fmtMoney(loan.loan_amount)}` : ''}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-800 px-3 pt-2 flex gap-1">
          {TAB_LIST.map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`px-3.5 py-2 text-xs font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${
                tab === k
                  ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                  : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
              }`}
              data-testid={`loan-drawer-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading || !loan ? (
            <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : tab === 'overview' ? (
            <OverviewTab loan={loan} onChanged={load} backendUrl={backendUrl} authHeaders={authHeaders} />
          ) : tab === 'activity' ? (
            <ActivityTab loanId={loanId} activities={activities} onAdded={(a) => { setActivities([a, ...activities]); onUpdated?.(); }} />
          ) : tab === 'nps' ? (
            <NPSTab loan={loan} backendUrl={backendUrl} authHeaders={authHeaders} onChanged={load} />
          ) : tab === 'messages' ? (
            <MessagesTab loanId={loanId} backendUrl={backendUrl} authHeaders={authHeaders} />
          ) : (
            <EditTab loan={loan} onSaved={() => { load(); onUpdated?.(); }} />
          )}
        </div>
      </aside>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-baseline gap-3 py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
    <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm text-slate-900 dark:text-white text-right break-words">{value || '—'}</span>
  </div>
);

const Block = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-2">{title}</h3>
    <div className="bg-slate-50 dark:bg-slate-900/60 rounded-xl px-3 py-2">{children}</div>
  </div>
);

const OverviewTab = ({ loan, onChanged, backendUrl, authHeaders }) => {
  const isDraft = (loan.enrolment_state || 'active') === 'draft';
  const isSuppressed = loan.do_not_contact || loan.deceased || ['discharged', 'refinanced_away', 'closed', 'exited'].includes(loan.lifecycle_status);
  return (
  <div className="px-5 py-5">
    {/* Draft enrolment banner — Slice A */}
    {isDraft && (
      <DraftApproval loanId={loan.id} backendUrl={backendUrl} authHeaders={authHeaders} onApproved={onChanged} />
    )}
    {/* Suppression banner */}
    {isSuppressed && !isDraft && (
      <div className="mb-4 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="loan-suppressed-banner">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-bold">Suppressed — automation paused.</div>
          {loan.deceased && <div className="mt-0.5">Marked deceased. No further automated touches will fire.</div>}
          {loan.do_not_contact && !loan.deceased && <div className="mt-0.5">Client requested no contact. Care queue and triggers will skip this loan.</div>}
          {['discharged','refinanced_away','closed','exited'].includes(loan.lifecycle_status) && <div className="mt-0.5">Lifecycle: <span className="font-semibold">{loan.lifecycle_status}</span>.</div>}
        </div>
      </div>
    )}

    <Block title="Client">
      <Row label="Name" value={loan.client_name} />
      <Row label="Phone" value={loan.client_phone} />
      <Row label="Email" value={loan.client_email} />
      <Row label="Entity" value={loan.entity_type} />
    </Block>
    <Block title="Loan">
      <Row label="Lender" value={loan.lender} />
      <Row label="Amount" value={fmtMoney(loan.loan_amount)} />
      <Row label="Settlement date" value={loan.settlement_date} />
      <Row label="Product" value={loan.product_type} />
      <Row label="Rate" value={`${Number(loan.interest_rate).toFixed(2)}% (${loan.rate_type})`} />
      <Row label="Repayment" value={loan.repayment_type} />
      <Row label="Term" value={`${loan.loan_term_years} yrs`} />
      <Row label="Purpose" value={loan.loan_purpose} />
    </Block>
    <Block title="Structure">
      <Row label="Fixed-rate expiry" value={loan.fixed_rate_expiry || '—'} />
      <Row label="Offset" value={loan.has_offset ? `Yes · ${fmtMoney(loan.offset_balance)}` : 'No'} />
      <Row label="Redraw" value={loan.has_redraw ? 'Yes' : 'No'} />
    </Block>
    <Block title="Property">
      <Row label="Address" value={loan.property_address} />
      <Row label="Value at settlement" value={fmtMoney(loan.property_value_at_settlement)} />
    </Block>
    <Block title="Commission">
      <Row label="Expected trail (bps)" value={loan.expected_trail_rate_bps} />
      <Row label="Expected monthly trail" value={fmtMoney(loan.expected_monthly_trail)} />
      <Row label="Clawback ends" value={loan.clawback_end_date} />
    </Block>
    <SuppressionPanel loan={loan} backendUrl={backendUrl} authHeaders={authHeaders} onChanged={onChanged} />
    {!isSuppressed && (
      <CloseLoanPanel loan={loan} backendUrl={backendUrl} authHeaders={authHeaders} onChanged={onChanged} />
    )}
  </div>
  );
};

// ── "Home loan closed" — hard-stops all future automated touch points ──
const CloseLoanPanel = ({ loan, backendUrl, authHeaders, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [lifecycle, setLifecycle] = useState('closed');
  const [reason, setReason] = useState('');

  const close = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ lifecycle });
      if (reason.trim()) params.append('reason', reason.trim());
      const r = await fetch(`${backendUrl}/api/settled-loans/${loan.id}/close?${params}`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail || 'Could not close the loan');
      }
      const j = await r.json();
      toast.success(`Home loan closed — ${j.tasks_cancelled} pending task${j.tasks_cancelled === 1 ? '' : 's'} cancelled`);
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="mt-4 mb-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/30 dark:hover:bg-rose-950/50 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-sm font-semibold"
          data-testid="close-loan-btn"
        >
          <CheckCircle2 className="w-4 h-4" /> Home loan closed
        </button>
        <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
          Use this when the loan&apos;s no longer active — paid off, refinanced away or otherwise closed. All pending care tasks will be cancelled and no further automated touch points will fire for this file.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 mb-2 px-4 py-3 rounded-xl bg-rose-50 dark:bg-rose-950/20 border-2 border-rose-200 dark:border-rose-900/50" data-testid="close-loan-confirm">
      <div className="flex items-start gap-2 mb-2">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-rose-600" />
        <p className="text-xs font-bold text-rose-900 dark:text-rose-200">Confirm: close this home loan?</p>
      </div>
      <p className="text-[11px] text-rose-800 dark:text-rose-300/80 mb-2.5">
        Every pending care task for {loan.client_name} will be cancelled and the cadence will stop. The loan record stays on file for trail / clawback reporting.
      </p>
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-rose-900 dark:text-rose-200 mb-1">Reason</label>
      <select
        className="w-full px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-900 mb-2"
        value={lifecycle}
        onChange={(e) => setLifecycle(e.target.value)}
        data-testid="close-loan-lifecycle"
      >
        <option value="closed">Closed (general)</option>
        <option value="discharged">Discharged — paid off in full</option>
        <option value="refinanced_away">Refinanced away to another lender</option>
      </select>
      <input
        type="text"
        className="w-full px-3 py-1.5 text-xs rounded-lg bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-900 mb-3"
        placeholder="Optional note (e.g. sold property, switched to ANZ, settled estate)…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        data-testid="close-loan-reason"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={close}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-50"
          data-testid="close-loan-confirm-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Yes, close this loan
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setReason(''); }}
          disabled={busy}
          className="px-3.5 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Slice A — Draft enrolment approval banner ────────────────────────
const DraftApproval = ({ loanId, backendUrl, authHeaders, onApproved }) => {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/${loanId}/approve-enrolment`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.detail?.message || err?.detail || 'Approval failed');
      }
      const j = await r.json();
      toast.success(`Enrolment approved — ${j.tasks_seeded} care task${j.tasks_seeded !== 1 ? 's' : ''} scheduled`);
      onApproved?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="mb-4 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-900 flex items-start gap-3" data-testid="draft-approval-banner">
      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-blue-700 dark:text-blue-300" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-blue-900 dark:text-blue-200">Draft — awaiting enrolment approval</div>
        <p className="text-xs text-blue-800 dark:text-blue-300/80 mt-1 mb-2">
          Care tasks haven&apos;t been seeded yet. Confirm the client&apos;s details (phone, email, suppression flags) below, then approve to activate the cadence.
        </p>
        <button
          type="button"
          onClick={approve}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50"
          data-testid="approve-enrolment-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Approve & start care cadence
        </button>
      </div>
    </div>
  );
};

// ── Slice A — Suppression flags panel ────────────────────────────────
const SuppressionPanel = ({ loan, backendUrl, authHeaders, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const flip = async (field, value, reason) => {
    setBusy(field);
    try {
      const params = new URLSearchParams({ [field]: String(value) });
      if (reason) params.append('reason', reason);
      const r = await fetch(`${backendUrl}/api/settled-loans/${loan.id}/suppression?${params}`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error('Update failed');
      toast.success('Suppression updated');
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  return (
    <div className="mb-2">
      <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-2">Suppression</h3>
      <div className="bg-slate-50 dark:bg-slate-900/60 rounded-xl p-3 space-y-2">
        <SuppressionToggle
          label="Do not contact"
          hint="Client asked us to stop. All automated touches will skip."
          checked={!!loan.do_not_contact}
          busy={busy === 'do_not_contact'}
          onChange={(v) => flip('do_not_contact', v)}
          dangerWhen={true}
          testid="dnc-toggle"
        />
        <SuppressionToggle
          label="Deceased"
          hint="Marks the client as deceased — hard-suppresses all automation immediately."
          checked={!!loan.deceased}
          busy={busy === 'deceased'}
          onChange={(v) => { if (v && !window.confirm('Mark this client as deceased and exit the enrolment? This cannot be undone via the UI.')) return; flip('deceased', v); }}
          dangerWhen={true}
          testid="deceased-toggle"
        />
        <SuppressionToggle
          label="Marketing consent"
          hint="Required for any non-transactional outreach (newsletters, rate broadcasts)."
          checked={!!loan.consent_marketing}
          busy={busy === 'consent_marketing'}
          onChange={(v) => flip('consent_marketing', v)}
          dangerWhen={false}
          testid="marketing-toggle"
        />
      </div>
    </div>
  );
};

const SuppressionToggle = ({ label, hint, checked, busy, onChange, dangerWhen, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      disabled={busy}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
      data-testid={testid}
    />
    <div className="flex-1 min-w-0">
      <div className={`text-sm font-semibold ${checked && dangerWhen ? 'text-red-700 dark:text-red-300' : 'text-slate-900 dark:text-white'}`}>{label}</div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug">{hint}</p>
    </div>
    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
  </label>
);

const ICONS = { call: Phone, sms: MessageSquare, email: Mail, meeting: Calendar, note: FileText, system: FileText, review: Calendar };

const ActivityTab = ({ loanId, activities, onAdded }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [text, setText] = useState('');
  const [type, setType] = useState('note');
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    const c = text.trim();
    if (!c) return;
    setSending(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/${loanId}/activities`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, content: c }),
      });
      if (!r.ok) throw new Error('Failed to log activity');
      const d = await r.json();
      onAdded(d);
      setText('');
      toast.success('Logged');
    } catch (err) { toast.error(err.message); } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <form onSubmit={send} className="px-5 pt-5 pb-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex gap-1.5 mb-2 flex-wrap">
          {['note', 'call', 'sms', 'email', 'meeting', 'review'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wide font-bold border ${
                type === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-300'
              }`}
              data-testid={`activity-type-${t}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder={`Log a ${type}…`}
            className="flex-1 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:outline-none focus:border-blue-400 resize-none"
            data-testid="activity-input"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="self-start rounded-full w-10 h-10 bg-blue-600 text-white grid place-items-center hover:bg-blue-700 disabled:opacity-50"
            data-testid="activity-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {activities.length === 0 ? (
          <p className="text-center text-xs text-slate-500 py-12">No activity yet — first note above.</p>
        ) : activities.map((a) => {
          const Icon = ICONS[a.type] || FileText;
          return (
            <div key={a.id} className="flex gap-3" data-testid="activity-row">
              <div className="h-8 w-8 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 grid place-items-center shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{a.type}</span>
                  <span className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </div>
                <p className="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap mt-0.5">{a.content}</p>
                {a.created_by_name && <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1"><UserIcon className="w-2.5 h-2.5" />{a.created_by_name}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EditTab = ({ loan, onSaved }) => {
  const { token } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [f, setF] = useState({
    client_phone: loan.client_phone || '',
    client_email: loan.client_email || '',
    interest_rate: loan.interest_rate ?? '',
    rate_type: loan.rate_type || 'Variable',
    fixed_rate_expiry: loan.fixed_rate_expiry || '',
    has_offset: !!loan.has_offset,
    offset_balance: loan.offset_balance ?? '',
    property_value_at_settlement: loan.property_value_at_settlement ?? '',
    notes: loan.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        interest_rate: f.interest_rate !== '' ? Number(f.interest_rate) : null,
        offset_balance: f.offset_balance !== '' ? Number(f.offset_balance) : null,
        property_value_at_settlement: f.property_value_at_settlement !== '' ? Number(f.property_value_at_settlement) : null,
        fixed_rate_expiry: f.fixed_rate_expiry || null,
      };
      const r = await fetch(`${backendUrl}/api/settled-loans/${loan.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Saved');
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const baseInput = 'w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:outline-none focus:border-blue-500';
  return (
    <form onSubmit={save} className="px-5 py-5 space-y-3">
      <p className="text-xs text-slate-500 italic mb-3">Common fields you&apos;ll touch most often. Full edit lives in Phase 2.</p>
      <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Phone</span><input className={baseInput} value={f.client_phone} onChange={(e) => set('client_phone', e.target.value)} /></label>
      <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Email</span><input className={baseInput} value={f.client_email} onChange={(e) => set('client_email', e.target.value)} /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Interest rate (%)</span><input type="number" step="0.01" className={baseInput} value={f.interest_rate} onChange={(e) => set('interest_rate', e.target.value)} /></label>
        <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Rate type</span>
          <select className={baseInput} value={f.rate_type} onChange={(e) => set('rate_type', e.target.value)}>
            {['Variable', 'Fixed', 'Split'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      {(f.rate_type === 'Fixed' || f.rate_type === 'Split') && (
        <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Fixed-rate expiry</span><input type="date" className={baseInput} value={f.fixed_rate_expiry} onChange={(e) => set('fixed_rate_expiry', e.target.value)} /></label>
      )}
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={f.has_offset} onChange={(e) => set('has_offset', e.target.checked)} />
        <span>Has offset</span>
      </label>
      {f.has_offset && (
        <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Offset balance</span><input type="number" step={100} className={baseInput} value={f.offset_balance} onChange={(e) => set('offset_balance', e.target.value)} /></label>
      )}
      <label className="flex flex-col gap-1 text-sm"><span className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Property value at settlement</span><input type="number" step={1000} className={baseInput} value={f.property_value_at_settlement} onChange={(e) => set('property_value_at_settlement', e.target.value)} /></label>
      <div className="pt-2 flex justify-end">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60" data-testid="loan-edit-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
        </button>
      </div>
    </form>
  );
};

// ── Slice B — NPS capture + history ──────────────────────────────────
const NPSTab = ({ loan, backendUrl, authHeaders, onChanged }) => {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [context, setContext] = useState('Adhoc');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);

  const loadHistory = React.useCallback(async () => {
    setLoadingHist(true);
    try {
      const r = await fetch(`${backendUrl}/api/settled-loans/nps/responses?loan_id=${loan.id}&days=730`, { headers: authHeaders });
      if (r.ok) setHistory(await r.json());
    } finally { setLoadingHist(false); }
  }, [backendUrl, authHeaders, loan.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async () => {
    if (score === null) return toast.error('Pick a score 0–10');
    setBusy(true);
    try {
      const params = new URLSearchParams({ score: String(score), context });
      if (comment.trim()) params.append('comment', comment.trim());
      const r = await fetch(`${backendUrl}/api/settled-loans/${loan.id}/nps?${params}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error('NPS save failed');
      const resp = await r.json();
      if (resp.category === 'Detractor') {
        toast.warning('Detractor — urgent 48h call task created', { description: 'Service recovery email queued.', duration: 7000 });
      } else if (resp.category === 'Promoter') {
        toast.success('Promoter — review request queued');
      } else {
        toast.success('Passive — soft follow-up queued');
      }
      setScore(null);
      setComment('');
      await loadHistory();
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const lastNps = history[0];

  return (
    <div className="px-5 py-5" data-testid="nps-tab">
      <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-2">Capture NPS</h3>
      <div className="bg-slate-50 dark:bg-slate-900/60 rounded-xl p-4 space-y-3 mb-5">
        <div className="text-xs text-slate-600 dark:text-slate-300 mb-1">
          On a scale of 0–10, how likely is <span className="font-semibold">{loan.client_name}</span> to recommend Derwent?
        </div>
        <div className="grid grid-cols-11 gap-1">
          {[0,1,2,3,4,5,6,7,8,9,10].map((n) => {
            const category = n <= 6 ? 'detractor' : n <= 8 ? 'passive' : 'promoter';
            const colour = category === 'detractor' ? 'red' : category === 'passive' ? 'amber' : 'emerald';
            const selected = score === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setScore(n)}
                data-testid={`nps-score-${n}`}
                className={`aspect-square text-sm font-bold rounded-lg border-2 transition-all ${
                  selected
                    ? colour === 'red'      ? 'bg-red-600 text-white border-red-600'
                    : colour === 'amber'    ? 'bg-amber-500 text-white border-amber-500'
                    :                         'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-blue-400'
                }`}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-slate-500 -mt-1">
          <span>Detractor (0–6)</span>
          <span>Passive</span>
          <span>Promoter (9–10)</span>
        </div>
        <select
          className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          data-testid="nps-context"
        >
          <option value="Adhoc">Ad-hoc capture</option>
          <option value="OneMonth">1-month survey</option>
          <option value="Annual">Annual review</option>
        </select>
        <textarea
          className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
          placeholder="What was the main reason for that score? (optional)"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          data-testid="nps-comment"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || score === null}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="nps-submit-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Record NPS
        </button>
      </div>

      <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-2">History</h3>
      {loadingHist ? (
        <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : history.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No NPS responses yet.</p>
      ) : (
        <ul className="space-y-2">
          {history.map((r) => {
            const colour = r.category === 'Detractor' ? 'red' : r.category === 'Passive' ? 'amber' : 'emerald';
            return (
              <li key={r.id} className="flex items-start gap-3 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-3" data-testid="nps-row">
                <div className={`h-10 w-10 rounded-xl grid place-items-center text-sm font-bold ${
                  colour === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                  : colour === 'amber' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                }`}>{r.score}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 dark:text-white">{r.category} · {r.context}</div>
                  {r.comment && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">&ldquo;{r.comment}&rdquo;</p>}
                  <p className="text-[10px] text-slate-400 mt-0.5">{new Date(r.received_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ── Slice D — Per-loan outbox (sent + queued messages) ───────────────
const STATUS_CHIP = {
  Queued:     'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  Sending:    'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  Sent:       'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  Delivered:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  Bounced:    'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  Failed:     'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  Suppressed: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
};

const MessagesTab = ({ loanId, backendUrl, authHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${backendUrl}/api/settled-loans/${loanId}/outbox`, { headers: authHeaders });
        if (r.ok) setItems(await r.json());
      } finally { setLoading(false); }
    })();
  }, [backendUrl, authHeaders, loanId]);

  if (loading) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="px-5 py-5" data-testid="messages-tab">
      <h3 className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-600 mb-2">Outbox — sends &amp; queued</h3>
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No messages yet for this loan. Templated sends are queued automatically by triggers and NPS routing.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li key={m.id} className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl p-3" data-testid="outbox-row">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide ${STATUS_CHIP[m.status] || STATUS_CHIP.Queued}`}>{m.status}</span>
                <span className="font-mono text-[10px] text-slate-400 uppercase">{m.template_code}</span>
                <span className="text-[10px] text-slate-500">{m.channel}</span>
                {m.template_version && <span className="text-[10px] text-slate-400">v{m.template_version}</span>}
              </div>
              <div className="text-xs text-slate-700 dark:text-slate-200 mt-1 break-all">→ {m.recipient}</div>
              {m.rendered_subject && <div className="text-xs italic text-slate-600 dark:text-slate-300 mt-0.5">&ldquo;{m.rendered_subject}&rdquo;</div>}
              <div className="text-[10px] text-slate-400 mt-1">
                {m.status === 'Queued' && <>Scheduled for {new Date(m.scheduled_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</>}
                {m.status === 'Sent' && m.sent_at && <>Sent {new Date(m.sent_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</>}
                {m.status === 'Delivered' && m.delivered_at && <>Delivered {new Date(m.delivered_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</>}
                {m.failure_reason && <span className="text-red-600 ml-1">· {m.failure_reason}</span>}
                {m.attempts > 0 && <span className="ml-1">· attempts: {m.attempts}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LoanDrawer;
